// Exports: AddSubtaskMemberModal
import React, { useEffect, useMemo, useState } from "react";
import { useAlert } from "components/UI_components/Alert";
import useTaskApi from "hooks/useTaskApi";

const AddSubtaskMemberModal = ({
  task,
  subtask,
  getInitials,
  onClose,
  onMembersUpdated,
  preserveScroll,
}) => {
  const { addAlert } = useAlert();
  const { getTaskMembers, updateSubtask } = useTaskApi();
  const [taskMembers, setTaskMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadTaskMembers = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getTaskMembers(task.id);
        if (!isMounted) return;

        if (response?.status) {
          setTaskMembers(response.data || []);
        } else {
          setError("Failed to fetch task members.");
        }
      } catch (fetchError) {
        if (isMounted) {
          setError("Error fetching task members.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadTaskMembers();

    return () => {
      isMounted = false;
    };
  }, [getTaskMembers, task.id]);

  useEffect(() => {
    if (!taskMembers.length) {
      setSelectedMembers([]);
      return;
    }

    const nextSelectedMembers = (subtask.assignee || [])
      .map((assignee) =>
        taskMembers.find(
          (taskMember) =>
            taskMember.id === assignee.member_id ||
            taskMember.member_id === assignee.member_id
        )
      )
      .filter(Boolean)
      .map((taskMember) => ({
        ...taskMember,
        member_id: taskMember.id ?? taskMember.member_id,
      }));

    setSelectedMembers(nextSelectedMembers);
  }, [subtask.assignee, taskMembers]);

  const normalizedTaskMembers = useMemo(
    () =>
      taskMembers.map((member) => ({
        ...member,
        member_id: member.id ?? member.member_id,
      })),
    [taskMembers]
  );

  const handleMemberSelect = (member) => {
    const memberId = member.member_id;
    setSelectedMembers((current) =>
      current.some((candidate) => candidate.member_id === memberId)
        ? current.filter((candidate) => candidate.member_id !== memberId)
        : [...current, member]
    );
  };

  const handleAddMembers = async () => {
    setLoading(true);
    setError(null);

    await preserveScroll(
      `[data-scroll-anchor="subtask-${subtask.id}"]`,
      async () => {
        try {
          const response = await updateSubtask(subtask.id, {
            assign_to: selectedMembers.map((member) => member.member_id),
            subtask_name: subtask.name,
            subtask_details: subtask.subtask_details,
            is_completed: subtask.is_completed,
          });

          if (response?.detail || response?.data?.detail) {
            addAlert(
              "You do not have permission to add members to subtask. You must have Task Authority Permission",
              "error"
            );
            return;
          }

          await onMembersUpdated(selectedMembers);
          onClose();
        } catch (updateError) {
          console.error("Error updating subtask members:", updateError);
          setError("Error updating subtask members.");
        } finally {
          setLoading(false);
        }
      }
    );
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg p-6 w-96"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Add Subtask Members</h2>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

        <div className="max-h-60 overflow-y-auto mb-4">
          {loading ? (
            <div className="text-gray-500 text-sm">Loading members...</div>
          ) : normalizedTaskMembers.length > 0 ? (
            normalizedTaskMembers.map((member) => (
              <div
                key={member.member_id}
                className={`flex items-center justify-between px-3 py-2 rounded cursor-pointer hover:bg-gray-100 ${
                  selectedMembers.some(
                    (candidate) => candidate.member_id === member.member_id
                  )
                    ? "bg-gray-200"
                    : ""
                }`}
                onClick={() => handleMemberSelect(member)}
              >
                <div className="flex items-center">
                  {member?.userpic ? (
                    <img
                      src={member.userpic}
                      alt={`${member.first_name} ${member.last_name}`}
                      className="w-8 h-8 rounded-full border-2 border-white mr-2"
                    />
                  ) : (
                    <span
                      className="w-8 h-8 rounded-full bg-[#343C6A] text-white font-medium text-sm flex items-center justify-center mr-2"
                      title={`${member.first_name} ${member.last_name}`}
                    >
                      {getInitials(`${member.first_name} ${member.last_name}`)}
                    </span>
                  )}
                  <span className="text-sm text-gray-700">
                    {member.first_name} {member.last_name}
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={selectedMembers.some(
                    (candidate) => candidate.member_id === member.member_id
                  )}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => handleMemberSelect(member)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  disabled={loading}
                />
              </div>
            ))
          ) : (
            <div className="text-gray-500 text-sm">
              No members available to add.
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2">
          <button
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-btn-bg text-white rounded hover:bg-purple-800"
            onClick={handleAddMembers}
            disabled={loading}
          >
            {loading ? "Adding..." : "Add Members"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSubtaskMemberModal;
